import Fuse from 'fuse.js';
import { CachedSearchedMarketsType } from 'src/interfaces/idashboard';

class MarketSearch {
  private markets: CachedSearchedMarketsType[];
  private fuse: Fuse<CachedSearchedMarketsType>;

  constructor(cachedSearchMarkets: {
    markets: CachedSearchedMarketsType[];
    fuse: Fuse<CachedSearchedMarketsType>;
  }) {
    this.markets = cachedSearchMarkets.markets;
    this.fuse = cachedSearchMarkets.fuse;
    return this;
  }

  calculate(input: string) {
    if (!input || input.trim() === '') {
      return [];
    }

    let finalResult: CachedSearchedMarketsType[] = [];
    const inputValueBreakDownArray: string[] =
      input.includes(',') || input.includes(' ')
        ? [
            input,
            ...input.split(/[,\s]+/).map(currentSpitedInputPart => currentSpitedInputPart.trim()),
          ]
        : [input];

    for (const currentInputChunk of inputValueBreakDownArray) {
      const results: CachedSearchedMarketsType[] = this.fuse
        .search(currentInputChunk, { limit: 7 })
        .map(data => data.item);
      const top7Results = results.slice(0, 7);

      finalResult = [...finalResult, ...top7Results];
    }
    // return finalResult;
    return this.sortSearchResults(finalResult, inputValueBreakDownArray).slice(0, 11);
  }

  formatSearchBarListingNames(district: string, city: string, country: string) {
    if (!district) return '';

    let result = district;
    if (city && city != '0') {
      result += ', ' + city;
    }
    result += ` ( ${country} )`;
    return result;
  }

  filterDuplicates(searchResults: CachedSearchedMarketsType[]) {
    /*
    Two markets are considered duplicates if:
      - They are both from the same country code and (have same city names or one of it dowsn't have a city name)
      - The names of the markets match under certain conditions:
          - Market A matches Market B exactly.
          - Market A matches the first part of Market B (e.g., "Dallas" matches "Dallas, Texas").
          - Market B matches the first part of Market A.

    If duplicate conditions are met, keep only one of the duplicates.
      - Keep the one with the highest priority.
      - If they have the same priority:
        - Keep the one which is verified.
        ------------------------------------------------------------------------------------------------
        - Keep the one without a square boundary if available. [TOOD]
        - If both have square boundaries, keep the one with greater supply. [TODO]
    */
    const seenNames = {}; // Hash table to store seen market names
    const filteredSearchResults: CachedSearchedMarketsType[] = [];

    searchResults.forEach(market => {
      const marketName = market.district.split(',')[0];
      const key = market.country + '-' + marketName;

      if (!seenNames[key]) {
        filteredSearchResults.push(market);
        seenNames[key] = true;
      } else {
        const existingMarket = filteredSearchResults.find(
          m =>
            isCountryMatching(m, market) &&
            isDistrictMatching(m, market) &&
            isCityMatching(m, market)
        );
        if (existingMarket) {
          if (market.priority < existingMarket.priority) {
            Object.assign(existingMarket, market);
          } else if (!existingMarket.isVerified && market.isVerified) {
            Object.assign(existingMarket, market);
          }
        } else {
          filteredSearchResults.push(market);
        }
      }
    });

    return filteredSearchResults;
  }

  getExactMatches(
    searchResults: CachedSearchedMarketsType[],
    inputs: string[],
    seenSearchNames: Set<string>
  ) {
    const results: CachedSearchedMarketsType[] = [];

    searchResults.forEach(searchResult => {
      const searchName = this.formatSearchBarListingNames(
        searchResult.district,
        searchResult.city,
        searchResult.country
      );

      inputs.forEach(input => {
        if (
          !seenSearchNames.has(searchName) &&
          (searchName.slice(0, input.length).toLowerCase() == input.toLowerCase() ||
            searchName
              .slice(0, input.length)
              .toLowerCase()
              .replace(/,/gi, '') == input.toLowerCase())
        ) {
          results.push(searchResult);
          seenSearchNames.add(searchName);
        }
      });
    });

    return results;
  }

  getPartialMatchs(
    searchResults: CachedSearchedMarketsType[],
    inputs: string[],
    seenSearchNames: Set<string>
  ) {
    const results: CachedSearchedMarketsType[] = [];

    searchResults.forEach(searchResult => {
      const searchName = this.formatSearchBarListingNames(
        searchResult.district,
        searchResult.city,
        searchResult.country
      );

      inputs.forEach(input => {
        if (!seenSearchNames.has(searchName) && searchName.search(new RegExp(input, 'ig')) > -1) {
          results.push(searchResult);
          seenSearchNames.add(searchName);
        }
      });
    });

    return results;
  }

  compareResults(result1, result2) {
    // Compare by lexical order of district
    const districtComparison = result1.district.localeCompare(result2.district);

    // If districts are the same, compare by isVerified
    if (districtComparison === 0) {
      // +true = 1, +false = 0
      const verifiedComparison = +result2.isVerified - +result1.isVerified;

      // If isVerified is the same, compare by priority
      return verifiedComparison === 0
        ? result1.priority.localeCompare(result2.priority)
        : verifiedComparison;
    }

    return districtComparison;
  }

  sortSearchResults(searchResults: CachedSearchedMarketsType[], inputTextArr: string[]) {
    console.log('Fuse results: ', searchResults);
    let exactMatches: CachedSearchedMarketsType[] = [];
    let partialMatches: CachedSearchedMarketsType[] = [];
    let nonMatches: CachedSearchedMarketsType[] = [];

    const filteredSearchResults = this.filterDuplicates(searchResults);
    const seenSearchNames = new Set<string>();

    exactMatches = this.getExactMatches(
      filteredSearchResults,
      inputTextArr.slice(0, 2),
      seenSearchNames
    );
    partialMatches = this.getPartialMatchs(filteredSearchResults, inputTextArr, seenSearchNames);

    nonMatches = filteredSearchResults.filter(searchResult => {
      const searchName = this.formatSearchBarListingNames(
        searchResult.district,
        searchResult.city,
        searchResult.country
      );
      if (!seenSearchNames.has(searchName)) {
        seenSearchNames.add(searchName);
        return true;
      }
      return false;
    });

    exactMatches = exactMatches.sort(this.compareResults);
    partialMatches = partialMatches.sort(this.compareResults);
    nonMatches = nonMatches.sort(this.compareResults); // supports fuzzy search

    return [...exactMatches, ...partialMatches, ...nonMatches];
  }
}

const isDistrictMatching = (searchResult1, searchResult2) => {
  const districtName1 = searchResult1.district.split(',')[0].toLowerCase();
  const districtName2 = searchResult2.district.split(',')[0].toLowerCase();
  return districtName1 === districtName2;
};

const isCityMatching = (searchResult1, searchResult2) => {
  const cityName1 = searchResult1.city;
  const cityName2 = searchResult2.city;
  return (
    !cityName1 ||
    !cityName2 ||
    cityName1 === '0' ||
    cityName2 === '0' ||
    cityName1.toLowerCase() === cityName2.toLowerCase()
  );
};

const isCountryMatching = (searchResult1, searchResult2) => {
  const countryName1 = searchResult1.country;
  const countryName2 = searchResult2.country;
  return countryName1 && countryName2 && countryName1.toLowerCase() === countryName2.toLowerCase();
};

export default MarketSearch;
